import { useState } from 'react';
import { ButtonType, CallToActionKind, CallToActionKindType } from '@/types';
import { useAppSelector } from '@/redux';
import { selectGuiLoadingState } from '@/redux/slices/gui.slice';

import CallToActionLabel from '@/components/common/callToAction/CallToActionLabel';
import MillesimaLink from '@/components/common/MillesimaLink';

import styles from './CallToActionContainer.module.scss';
import { DESIGN_SYSTEM_SIZES, DesignSystemSizesTypes } from '@/constants/styles';

export type CallToActionContainerProps = {
  id?: string;
  kind?: CallToActionKindType;
  buttonRef?: React.LegacyRef<HTMLButtonElement> | React.MutableRefObject<HTMLButtonElement>;
  className?: string;
  labelClassName?: string;
  onClick?: (event: any) => void;
  type?: ButtonType;
  disabled?: boolean;
  children?: React.ReactNode;
  icon?: {
    url: string;
    alt?: string;
    width?: number;
    height?: number;
    rightSide?: boolean;
  };
  fullWidth?: boolean;
  fullWidthOnMobile?: boolean;
  title?: string;
  sensefuelId?: string;
  withLink?: boolean;
  cta?: {
    url: string;
    text: {
      label: string;
      color?: string;
      hoverColor?: string;
    };
    background?: {
      color?: string;
      hoverColor?: string;
    };
    isNofollow?: boolean;
  }
  isNofollow?: boolean;
  withDefaultBorder?: boolean;
  isTargetBlank?: boolean;
  buttonSize?: ExternalInstantiation;
  form?: string;
  shouldShowLoadingState?: boolean;
}

type ExternalInstantiation = Exclude<DesignSystemSizesTypes, 'medium'>
type InternalInstantiation = Exclude<DesignSystemSizesTypes, 'small' | 'large'>

type CallToActionInternalProps = {
  buttonSize?: DesignSystemSizesTypes;
} & CallToActionContainerProps;

export const CallToActionContainer: React.FC<CallToActionInternalProps> = ({
  id,
  kind = CallToActionKind.PRIMARY,
  buttonRef,
  className = '',
  labelClassName = '',
  onClick,
  type,
  disabled,
  children,
  icon,
  fullWidth,
  fullWidthOnMobile = true,
  title,
  sensefuelId,
  withLink,
  cta,
  isNofollow,
  withDefaultBorder,
  shouldShowLoadingState = false,
  buttonSize = DESIGN_SYSTEM_SIZES.MEDIUM satisfies InternalInstantiation,
  ...props
}) => {
  const isMobileDevice = useAppSelector(({ gui }) => gui.isMobileDevice);
  const loadingState = useAppSelector(selectGuiLoadingState);
  const [hover, setHover] = useState(false);

  const isIconOnly = !withLink && icon && !children;
  const testRef = `button${id ? '-' + id : ''}`;

  const iconOnlyStyle = isIconOnly && styles['icon-only'];

  const classNameValue = [
    styles.btn,
    styles[kind],
    iconOnlyStyle,
    styles[buttonSize],
    className,
    (fullWidth || (isMobileDevice && fullWidthOnMobile)) && styles.fullwidth,
    icon?.rightSide && styles.right
  ].filter(Boolean).join(' ');

  const labelCustomClassName = [
    styles.label,
    labelClassName,
  ].filter(Boolean).join(' ');

  /** Link elements */
  const bgColor = hover ? cta?.background?.hoverColor : cta?.background?.color;
  const styleContainer = cta && {
    background: bgColor,
    borderColor: !withDefaultBorder && bgColor
  };
  const styleContent = cta && {
    color: hover ? cta.text?.hoverColor : cta.text?.color
  };
  const customClassName = withLink && {
    link: classNameValue
  };

  const buttonLoadingStateWidth = (buttonRef as React.MutableRefObject<HTMLButtonElement>)?.current?.clientWidth;

  if (loadingState && shouldShowLoadingState) {
    return (
      <button
        id={id}
        className={classNameValue}
        data-rbf={testRef}
        type={type}
        title={title}
        data-test-id={testRef}
        data-sensefuel-id={sensefuelId}
        disabled
        {...props}
        style={{ minWidth: `${buttonLoadingStateWidth}px` }}
      >
        <span className={styles.spinner} />
      </button>
    );
  }

  return withLink ? (
    // link version, no disable option on link html
    <MillesimaLink
      id={id}
      href={cta?.url}
      onClick={onClick}
      customClassName={customClassName}
      data-test-id={testRef}
      data-sensefuel-id={sensefuelId}
      isWithoutContainer
      style={styleContainer}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      isNofollow={isNofollow}
      {...props}
    >
      <CallToActionLabel icon={icon} label={cta?.text.label} className={labelCustomClassName} style={styleContent}/>
    </MillesimaLink>
  ) : (
    <button
      id={id}
      ref={buttonRef}
      onClick={onClick}
      className={classNameValue}
      data-rbf={testRef}
      type={type}
      disabled={disabled}
      title={title}
      data-test-id={testRef}
      data-sensefuel-id={sensefuelId}
      {...props}
    >
      <CallToActionLabel icon={icon} label={children} className={labelCustomClassName} />
    </button>
  );
};

export default CallToActionContainer;
